/**
 * Type-aware comparison rows.
 *
 * Only listings of the SAME ad type can share a comparison (the fields mean
 * different things across types), so each type gets its own row set — the
 * same specifications the listing detail page shows, in the order a buyer
 * evaluates them.
 *
 * `better: 'low' | 'high'` marks the rows where a best-value hint is
 * objectively meaningful (price, year). Everything else is shown as stored.
 */
const yn = (v) => (v === null || v === undefined ? null : (v ? 'Yes' : 'No'));
const money = (v) => (v == null || v === '' ? null : `₹ ${Number(v).toLocaleString('en-IN')}`);
const joinList = (v) => (Array.isArray(v) && v.length ? v.join(', ') : null);

const RESPONSE_LABEL = {
  same_day: 'Same-day', '24h': 'Within 24h', '48h': 'Within 48h', week: 'Within a week',
};

const priceRow = (type) => {
  if (type === 'sell') return { key: 'price', label: 'Asking price', get: (l) => money(l.asking_price), raw: (l) => l.asking_price, better: 'low' };
  if (type === 'wanted') return { key: 'price', label: 'Budget', get: (l) => money(l.budget_amount), raw: (l) => l.budget_amount, better: 'high' };
  if (type === 'rent') return { key: 'price', label: 'Rental rate', get: (l) => money(l.price_amount), raw: (l) => l.price_amount, better: 'low' };
  return { key: 'price', label: 'Starting price', get: (l) => money(l.starting_price_inr), raw: (l) => l.starting_price_inr, better: 'low' };
};

const COMMON_HEAD = [
  { key: 'category', label: 'Category', get: (l) => l.category_name },
  { key: 'subcategory', label: 'Subcategory', get: (l) => l.subcategory_name },
  { key: 'location', label: 'Location', get: (l) => [l.city, l.state].filter(Boolean).join(', ') || null },
  { key: 'country', label: 'Country', get: (l) => l.country },
];

const COMMON_TAIL = [
  { key: 'seller', label: 'Seller', get: (l) => l.company_name_snapshot },
  { key: 'gst', label: 'GST verified', get: (l) => yn(!!l.is_gst_verified) },
  { key: 'views', label: 'Views', get: (l) => (l.view_count != null ? String(l.view_count) : null) },
];

const BY_TYPE = {
  sell: [
    { key: 'condition', label: 'Condition', get: (l) => l.condition },
    { key: 'brand', label: 'Brand', get: (l) => l.brand },
    { key: 'model', label: 'Model', get: (l) => l.model },
    { key: 'year', label: 'Year of manufacture', get: (l) => (l.year_of_manufacture ? String(l.year_of_manufacture) : null), raw: (l) => l.year_of_manufacture, better: 'high' },
    { key: 'hours', label: 'Running hours', get: (l) => l.running_hours },
    { key: 'quantity', label: 'Quantity', get: (l) => [l.quantity, l.unit].filter(Boolean).join(' ') || null },
    { key: 'origin', label: 'Country of origin', get: (l) => l.country_of_origin },
    { key: 'price_type', label: 'Pricing basis', get: (l) => l.pricing_display || l.price_type },
  ],
  wanted: [
    { key: 'pref_condition', label: 'Preferred condition', get: (l) => l.preferred_condition },
    { key: 'brand', label: 'Preferred brand', get: (l) => l.brand },
    { key: 'qty_needed', label: 'Quantity needed', get: (l) => l.quantity_needed },
    { key: 'urgency', label: 'Urgency', get: (l) => l.urgency },
    { key: 'budget_type', label: 'Budget basis', get: (l) => l.budget_type },
    { key: 'supply_loc', label: 'Preferred supply location', get: (l) => l.preferred_supply_location },
  ],
  rent: [
    { key: 'condition', label: 'Condition', get: (l) => l.condition },
    { key: 'brand', label: 'Brand', get: (l) => l.brand },
    { key: 'model', label: 'Model', get: (l) => l.model },
    { key: 'pricing_type', label: 'Rate basis', get: (l) => l.pricing_display || l.pricing_type },
    { key: 'deposit', label: 'Security deposit', get: (l) => money(l.security_deposit), raw: (l) => l.security_deposit, better: 'low' },
    { key: 'availability', label: 'Availability', get: (l) => l.availability_status },
    { key: 'available_from', label: 'Available from', get: (l) => l.available_from },
    { key: 'min_period', label: 'Minimum rental period', get: (l) => l.minimum_rental_period },
    { key: 'max_period', label: 'Maximum rental period', get: (l) => l.maximum_rental_period },
    { key: 'operator', label: 'Operator included', get: (l) => yn(l.operator_included) },
    { key: 'transport', label: 'Transport included', get: (l) => yn(l.transport_included) },
    { key: 'qty_available', label: 'Quantity available', get: (l) => [l.quantity_available, l.unit].filter(Boolean).join(' ') || null },
  ],
  service: [
    { key: 'experience', label: 'Experience', get: (l) => (l.experience_years != null ? `${l.experience_years} yrs` : null), raw: (l) => l.experience_years, better: 'high' },
    { key: 'pricing_model', label: 'Pricing model', get: (l) => l.pricing_model },
    { key: 'service_mode', label: 'Service mode', get: (l) => l.service_mode },
    { key: 'travel', label: 'Travel capability', get: (l) => l.travel_capability },
    { key: 'response', label: 'Response time', get: (l) => RESPONSE_LABEL[l.response_time] || l.response_time },
    { key: 'emergency', label: 'Emergency availability', get: (l) => (l.emergency_service === '24x7' ? '24×7' : l.emergency_service) },
    { key: 'oem', label: 'OEM authorised', get: (l) => yn(l.oem_authorized) },
    { key: 'oem_brand', label: 'Authorised brand', get: (l) => l.authorized_brand },
    { key: 'visit_charge', label: 'Visit charge', get: (l) => l.visit_charge },
    { key: 'certs', label: 'Certifications', get: (l) => joinList(l.certifications) },
    { key: 'industries', label: 'Industries served', get: (l) => joinList(l.industries_served) },
  ],
};

export const compareRowsFor = (type) => [
  priceRow(type || 'sell'),
  ...COMMON_HEAD,
  ...(BY_TYPE[type] || BY_TYPE.sell),
  ...COMMON_TAIL,
];

/** Index of the objectively best cell for a row, or -1 when not applicable. */
export const bestIndexFor = (row, listings) => {
  if (!row.better || !row.raw) return -1;
  const nums = listings.map((l) => {
    const v = l && row.raw(l);
    return v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
  });
  const present = nums.filter((n) => n != null);
  if (present.length < 2) return -1;
  const target = row.better === 'low' ? Math.min(...present) : Math.max(...present);
  if (present.filter((n) => n === target).length > 1) return -1; // a tie isn't a winner
  return nums.indexOf(target);
};
